import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Pagination, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeDetailTabDefinition,
  ChangeDetailTabEntryDetail,
  ChangeDetailTabEntrySlim,
  ChangeDetailTabField,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import ChangeDetailTabFieldInput from './ChangeDetailTabFieldInput';

interface Props {
  changeId: number;
  tab: ChangeDetailTabDefinition;
  active: boolean;
}

type FormMode = 'create' | 'edit' | 'view';

const ChangeDetailTabEntriesPanel: React.FC<Props> = ({ changeId, tab, active }) => {
  const [items, setItems] = useState<ChangeDetailTabEntrySlim[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<FormMode>('view');
  const [activeEntry, setActiveEntry] = useState<ChangeDetailTabEntryDetail | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string | number>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const columns = useMemo(
    () => (tab.fields || []).filter((f) => f.is_visible).slice(0, 4),
    [tab.fields],
  );

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try {
      const data = await changesApi.listDetailTabEntries(changeId, tab.id, { page, limit, q: q || undefined });
      setItems(data.items);
      setTotal(data.total);
      setCanCreate(data.can_create);
      setCanEdit(data.can_edit);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar registros.'));
    } finally {
      setLoading(false);
    }
  }, [active, changeId, limit, page, q, tab.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    const initial: Record<string, string | number> = {};
    tab.fields.forEach((field) => {
      initial[field.field_key] = field.field_type === 'user_picker' ? 0 : '';
    });
    setFormValues(initial);
    setActiveEntry(null);
    setModalMode('create');
    setFormError(null);
    setModalOpen(true);
  };

  const openEntry = async (entryId: number, mode: FormMode) => {
    setFormError(null);
    try {
      const detail = await changesApi.getDetailTabEntry(changeId, tab.id, entryId);
      setActiveEntry(detail);
      const values: Record<string, string | number> = {};
      detail.fields.forEach((row) => {
        if (row.field_type === 'user_picker') {
          values[row.field_key] = row.user_id || Number(row.value) || 0;
        } else {
          values[row.field_key] = row.value || '';
        }
      });
      setFormValues(values);
      setModalMode(mode);
      setModalOpen(true);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao abrir registro.'));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (modalMode === 'create') {
        await changesApi.createDetailTabEntry(changeId, tab.id, formValues);
      } else if (modalMode === 'edit' && activeEntry) {
        await changesApi.updateDetailTabEntry(changeId, tab.id, activeEntry.id, formValues);
      }
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      setFormError(getApiErrorMessage(err, 'Não foi possível salvar.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeEntry || modalMode !== 'edit') return;
    setSaving(true);
    try {
      await changesApi.deleteDetailTabEntry(changeId, tab.id, activeEntry.id);
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      setFormError(getApiErrorMessage(err, 'Não foi possível excluir.'));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const renderField = (field: ChangeDetailTabField, readOnly: boolean) => (
    <Form.Group className="mb-3" key={field.id}>
      <Form.Label>
        {field.label}
        {field.is_required ? ' *' : ''}
      </Form.Label>
      {field.help_text ? <div className="text-muted small mb-1">{field.help_text}</div> : null}
      <ChangeDetailTabFieldInput
        field={field}
        value={formValues[field.field_key] ?? (field.field_type === 'user_picker' ? 0 : '')}
        disabled={readOnly}
        onChange={(next) => setFormValues((prev) => ({ ...prev, [field.field_key]: next }))}
      />
    </Form.Group>
  );

  if (!active) return null;

  return (
    <div>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {tab.enable_search ? (
          <Form.Control
            type="search"
            size="sm"
            className="me-auto"
            style={{ maxWidth: 280 }}
            placeholder="Buscar registros…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                setQ(searchInput.trim());
              }
            }}
          />
        ) : (
          <div className="me-auto" />
        )}
        {canCreate && tab.enable_create ? (
          <Button size="sm" variant="primary" onClick={openCreate}>
            Novo
          </Button>
        ) : null}
      </div>
      {loading ? (
        <p className="text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Carregando…
        </p>
      ) : (
        <Table responsive hover size="sm" className="align-middle">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.id}>{col.label}</th>
              ))}
              <th className="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-muted text-center py-4">
                  Nenhum registro ainda.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id}>
                  {columns.map((col) => (
                    <td key={col.id}>{row.display[col.field_key] || '—'}</td>
                  ))}
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => void openEntry(row.id, 'view')}
                    >
                      Ver
                    </Button>
                    {canEdit ? (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="ms-1"
                        onClick={() => void openEntry(row.id, 'edit')}
                      >
                        Editar
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
      {tab.enable_pagination && totalPages > 1 ? (
        <Pagination size="sm" className="mb-0">
          <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
          <Pagination.Item active>{page}</Pagination.Item>
          <Pagination.Next
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Pagination>
      ) : null}

      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Novo registro' : modalMode === 'edit' ? 'Editar registro' : 'Registro'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError ? <Alert variant="danger">{formError}</Alert> : null}
          {tab.fields.filter((f) => f.is_visible).map((field) => renderField(field, modalMode === 'view'))}
          {activeEntry && modalMode === 'view' ? (
            <div className="text-muted small border-top pt-3 mt-2">
              <div>
                Criado por {activeEntry.created_by.name || `#${activeEntry.created_by.id}`} em{' '}
                {new Date(activeEntry.created_at).toLocaleString('pt-BR')}
              </div>
              {activeEntry.updated_by ? (
                <div>
                  Última alteração por {activeEntry.updated_by.name || `#${activeEntry.updated_by.id}`} em{' '}
                  {new Date(activeEntry.updated_at).toLocaleString('pt-BR')}
                </div>
              ) : null}
              <div className="mt-1">
                <Link to="/changes/auditoria">Ver auditoria de mudanças</Link>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {modalMode === 'edit' && canEdit ? (
            <Button variant="outline-danger" className="me-auto" disabled={saving} onClick={() => void handleDelete()}>
              Excluir
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => setModalOpen(false)}>
            Fechar
          </Button>
          {modalMode !== 'view' && canEdit ? (
            <Button variant="primary" disabled={saving} onClick={() => void handleSave()}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChangeDetailTabEntriesPanel;
